/**
 * ComplianceRulesSection — embedded panel on /compliance.
 *
 * Lists rule rows grouped by rule_key (AML, MiFID Suitability, Person KYC).
 * Admin / compliance get an "Edit" action that opens a modal with a
 * NumberInput for interval_months. Other roles see a read-only table.
 *
 * On a successful save the response is a RecomputeReport — we surface
 * "applied to N agreements / M person reviews" via a toast so the maker
 * knows the cascade fired.
 */
import {
  ActionIcon, Badge, Button, Card, Group, Modal, NumberInput,
  Stack, Table, Text, TextInput, Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPencil, IconScale } from '@tabler/icons-react';
import { useState } from 'react';

import { usePermissions } from '../hooks/usePermissions';
import {
  useComplianceRules,
  useUpdateComplianceRule,
} from '../hooks/useComplianceRules';
import type { ComplianceRule } from '../api/types';
import { useAlertModal } from './AlertModalProvider';

const RULE_KEY_LABELS: Record<string, string> = {
  AML_REVIEW: 'AML review',
  MIFID_SUITABILITY_REVIEW: 'MiFID suitability review',
  // C-114 (2026-06-10): migration 0009 renamed the rule key; the stale
  // PERSON_KYC_REVIEW entry here matched the stale reseed_rules.py seed
  // and masked the drift. Old key kept as a fallback label so a database
  // that still carries the stale row is VISIBLY flagged instead of
  // rendering a normal-looking section.
  AGREEMENT_KYC_REVIEW: 'Agreement KYC review',
  PERSON_KYC_REVIEW: 'Person KYC review (STALE KEY — run reseed_rules)',
};

function formatAppliesWhen(applies: Record<string, string>): string {
  const entries = Object.entries(applies || {});
  if (entries.length === 0) return 'all rows';
  return entries.map(([k, v]) => `${k} = ${v}`).join(', ');
}

function EditRuleModal({
  rule, opened, onClose,
}: {
  rule: ComplianceRule;
  opened: boolean;
  onClose: () => void;
}) {
  const [months, setMonths] = useState<number | string>(rule.interval_months);
  const [description, setDescription] = useState<string>(rule.description ?? '');
  const updateRule = useUpdateComplianceRule(rule.id);
  const { showAlert } = useAlertModal();

  // #192 polish 2026-06-08: prevent invalid submission instead of
  // showing an errors-as-toast (anti-pattern §5.E). Mantine NumberInput
  // with min/max + clampBehavior="strict" already caps the value at the
  // UI layer; this is the belt-and-braces gate that also covers any
  // non-numeric residual state.
  const monthsNum = typeof months === 'number' ? months : Number(months);
  const monthsInvalid = !Number.isFinite(monthsNum) || monthsNum < 1 || monthsNum > 240;

  function handleSave() {
    if (monthsInvalid) return;  // defensive; Save button is disabled in this state
    updateRule.mutate(
      { interval_months: monthsNum, description: description || null },
      {
        onSuccess: (rep) => {
          const a = rep.recomputed.agreements ?? 0;
          const parts: string[] = [];
          if (a) parts.push(`${a} agreement${a === 1 ? '' : 's'}`);
          const cascade = parts.length > 0
            ? `Recomputed: ${parts.join(' + ')}.`
            : 'No downstream rows affected.';
          notifications.show({
            color: 'green', icon: <IconCheck size={16} />,
            title: 'Rule updated',
            message: cascade,
          });
          onClose();
        },
        // #192 polish 2026-06-08: save error inside a Modal — Edge case 6
        // (modal-stays-modal). showAlert renders a second modal over the
        // form modal so the error is visible in the user's current context,
        // not as a top-right toast that can be dismissed before reading.
        onError: (e) => showAlert({
          color: 'red',
          title: 'Save failed',
          message: e.detail || 'Unknown error',
        }),
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconScale size={18} />
          <Text fw={600}>Edit rule: {RULE_KEY_LABELS[rule.rule_key] ?? rule.rule_key}</Text>
        </Group>
      }
      centered
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Applies to: <Text span fw={500} c="dark">{formatAppliesWhen(rule.applies_when)}</Text>
        </Text>
        <NumberInput
          label="Interval (months)"
          description="The maximum time between reviews. 1 to 240."
          value={months}
          onChange={(v) => setMonths(v)}
          min={1}
          max={240}
          clampBehavior="strict"
          allowDecimal={false}
          required
        />
        <TextInput
          label="Description"
          description="Free-text explanation visible to other compliance staff."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            loading={updateRule.isPending}
            disabled={monthsInvalid}
          >
            Save and recompute
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RuleRow({ rule, canEdit }: { rule: ComplianceRule; canEdit: boolean }) {
  const [opened, { open, close }] = useDisclosure(false);
  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Text size="sm">{formatAppliesWhen(rule.applies_when)}</Text>
        </Table.Td>
        <Table.Td>
          <Badge variant="light" color="azul">{rule.interval_months} months</Badge>
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed">{rule.description ?? '—'}</Text>
        </Table.Td>
        <Table.Td style={{ width: 70 }}>
          {canEdit && (
            <ActionIcon variant="subtle" onClick={open} title="Edit interval">
              <IconPencil size={16} />
            </ActionIcon>
          )}
        </Table.Td>
      </Table.Tr>
      {canEdit && opened && (
        <EditRuleModal rule={rule} opened={opened} onClose={close} />
      )}
    </>
  );
}

export function ComplianceRulesSection() {
  const { can } = usePermissions();
  const canEdit = can('compliance_rule.manage');
  const rulesQ = useComplianceRules();

  if (rulesQ.isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">Loading rules…</Text>
      </Card>
    );
  }
  if (rulesQ.error) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="red">Could not load compliance rules: {rulesQ.error.detail}</Text>
      </Card>
    );
  }

  const rules = rulesQ.data?.items ?? [];
  // Group by rule_key for display.
  const byKey = new Map<string, ComplianceRule[]>();
  for (const r of rules) {
    const arr = byKey.get(r.rule_key) ?? [];
    arr.push(r);
    byKey.set(r.rule_key, arr);
  }
  // Stable display order: AML, MIFID, KYC (stale pre-0009 key last).
  const orderedKeys = Array.from(byKey.keys()).sort((a, b) => {
    const order = ['AML_REVIEW', 'MIFID_SUITABILITY_REVIEW', 'AGREEMENT_KYC_REVIEW', 'PERSON_KYC_REVIEW'];
    return order.indexOf(a) - order.indexOf(b);
  });

  return (
    <Card withBorder p="lg" radius="md">
      <Stack>
        <Group gap="xs" align="center">
          <IconScale size={20} />
          <Title order={4}>Compliance rules</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Review-interval policies driving the <strong>next-review-by</strong> dates on agreements
          and person reviews. {canEdit
            ? 'Changing an interval recomputes all affected rows immediately.'
            : 'Read-only — only Admin and Compliance can edit.'}
        </Text>

        {orderedKeys.map((key) => (
          <Stack key={key} gap="xs">
            <Text size="sm" fw={600}>{RULE_KEY_LABELS[key] ?? key}</Text>
            <Table withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Applies to</Table.Th>
                  <Table.Th style={{ width: 130 }}>Interval</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ width: 70 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {byKey.get(key)!.map((r) => (
                  <RuleRow key={r.id} rule={r} canEdit={canEdit} />
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}
